import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Editor, loader } from "@monaco-editor/react"
import { Typography } from "@synapse-analytics/synapse-ui"

import { getTheme } from "../../hooks/UseTheme"
import { KonanCopyToClipboard } from "../utility/KonanCopyToClipboard"

import styles from "./MonacoTextEditor.module.scss"

export type Props = {
  value: string | undefined
  language: string
  height?: string
  width?: string
  readOnly?: boolean
  onChange?: (text: string | undefined) => void
  loading?: React.ReactNode
  automaticLayout?: boolean
  lineNumbers?: "on" | "off"
  renderLineHighLight?: "line" | "all" | "none" | "gutter"
  enableCopy?: boolean
  wordWrap?: "on" | "off"
  borderRadius?: string
  numbersColumnWidth?: number
}

export const MonacoTextEditor = (props: Props): React.ReactElement => {
  const {
    value,
    language,
    height,
    width = "100%",
    automaticLayout = false,
    readOnly = false,
    onChange,
    loading,
    lineNumbers = "on",
    wordWrap,
    enableCopy = false,
    renderLineHighLight = "line",
    borderRadius = "8px",
    numbersColumnWidth = 3,
  } = props

  const theme = getTheme()

  const { t } = useTranslation("dataDisplay")

  loader.config({
    paths: {
      vs: "https://cdnjs.cloudflare.com/ajax/libs/monaco-editor/0.36.1/min/vs",
    },
  })

  const [newHeight, setNewHeight] = useState<number | string>(
    height ?? ((value?.split("\n").length as number) + 1) * 23,
  )

  // automatically layout the height of the code editor based on the input value
  useEffect(() => {
    if (automaticLayout) {
      const length = (value?.split("\n").length as number) + 0.5
      setNewHeight(`${length * 23}px`)
    }
  }, [automaticLayout, value])

  return (
    <div className={styles.MonacoTextEditor} style={{ width, borderRadius: borderRadius }}>
      {enableCopy && value && (
        <div className={styles.copyBtnContainer}>
          <KonanCopyToClipboard text={value} rounded />
        </div>
      )}
      <Editor
        height={automaticLayout ? newHeight : height}
        width={width}
        language={language}
        value={value}
        theme={theme.mode === "dark" ? "vs-dark" : "vs-light"}
        options={{
          minimap: { enabled: false },
          readOnly: readOnly,
          scrollBeyondLastLine: true,
          autoIndent: "advanced",
          formatOnType: true,
          formatOnPaste: true,
          fontFamily: '"Inter", sans-serif',
          fontSize: 16,
          lineNumbers: lineNumbers,
          padding: { top: 8 },
          matchBrackets: "near",
          detectIndentation: true,
          smoothScrolling: true,
          wordWrap: wordWrap,
          wrappingStrategy: "advanced",
          renderLineHighlight: renderLineHighLight,
          lineNumbersMinChars: numbersColumnWidth,
        }}
        onChange={(e) => onChange?.(e)}
        loading={loading ?? <Typography variant="h3-regular">{t("monacoTextEditor.loading")}</Typography>}
      />
    </div>
  )
}
